import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, CheckCircle2, XCircle, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import {
  IconButton,
  UniversalModuleView,
} from '../../../ui-components/listing';
import Dropdown from '../../../ui-components/Dropdown';
import Toggle from '../../../ui-components/Toggle';
import {
  useGetUserListQuery,
  useChangeUserStatusMutation,
  useResendUserVerificationEmailMutation,
} from './usersApi';
import type { User as AppUser, UserListRequest } from './model';
import StatusTag from '../../../ui-components/StatusTag';

const VerificationBadge: React.FC<{
  isVerified?: boolean;
}> = ({ isVerified }) => {
  const verified = Boolean(isVerified);

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        verified
          ? 'border border-success-200 bg-success-100 text-success-700'
          : 'border border-warning-200 bg-warning-100 text-warning-700'
      }`}
    >
      {verified ? (
        <CheckCircle2 className="h-2.5 w-2.5" />
      ) : (
        <XCircle className="h-2.5 w-2.5" />
      )}
      {verified ? 'Verified' : 'Not Verified'}
    </span>
  );
};

// ─── Date formatter ──────────────────────────────────────────────────────────────
const formatDate = (iso: any) => {
  if (!iso) return '—';
  try {
    const date = typeof iso === 'string' ? new Date(iso) : iso;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso);
  }
};

// ─── Column definitions ──────────────────────────────────────────────────────────
const columns: ColDef<AppUser>[] = [
  {
    headerName: 'NAME',
    field: 'fullName',
    sortable: true,
    minWidth: 160,
    cellClass: 'font-medium text-brand-600',
  },
  {
    headerName: 'EMAIL',
    field: 'email',
    sortable: true,
    minWidth: 220,
    valueGetter: (params: ValueGetterParams<AppUser>) =>
      params.data?.email ?? '—',
  },
  {
    headerName: 'VERIFICATION STATUS',
    field: 'isVerified',
    sortable: false,
    minWidth: 170,
  },
  {
    headerName: 'STATUS',
    field: 'status',
    sortable: false,
    minWidth: 120,
  },
  {
    headerName: 'EVENTS CREATED',
    field: 'eventCreated',
    sortable: true,
    minWidth: 140,
    cellClass: 'text-center',
    headerClass: 'text-center',
  },
  {
    headerName: 'EVENTS JOINED',
    field: 'eventJoined',
    sortable: true,
    minWidth: 130,
    cellClass: 'text-center',
    headerClass: 'text-center',
  },
  {
    headerName: 'CREATED DATE',
    field: 'createdDt',
    sortable: true,
    minWidth: 140,
    cellClass: 'text-center',
    headerClass: 'text-center justify-center',
    valueFormatter: (params) => formatDate(params.value),
  },
  {
    headerName: 'ACTION',
    field: '__actions',
    sortable: false,
    minWidth: 180,
  },
];

// ─── Main Page Component ─────────────────────────────────────────────────────────
const UsersPage: React.FC = () => {
  const navigate = useNavigate();

  // ── Filter State ────────────────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [refreshToken, setRefreshToken] = useState(0);

  // ── Query state (for warmed cache) ──────────────────────────────────────────
  const queryParams: UserListRequest = useMemo(
    () => ({
      page: 1,
      size: 10,
      status: filterStatus || undefined,
      sortBy: 'createdDt',
      sortDirection: 'desc',
      isDeleted: false,
    }),
    [filterStatus]
  );

  const { refetch } = useGetUserListQuery(queryParams);
  const [changeStatus, { isLoading: isStatusUpdating }] =
    useChangeUserStatusMutation();
  const [resendVerificationEmail, { isLoading: isResendingVerification }] =
    useResendUserVerificationEmailMutation();

  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  const handleStatusToggle = async (user: AppUser) => {
    const nextStatus = user.status === 'active' ? 'inactive' : 'active';
    setTogglingUserId(user.id);
    try {
      await changeStatus({
        id: user.id,
        status: nextStatus,
      }).unwrap();
      handleRefresh();
    } catch (error: any) {
      toast.error(error?.data?.settings?.message || 'Failed to update status');
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleRefresh = useCallback(() => {
    refetch();
    setRefreshToken((prev) => prev + 1);
  }, [refetch]);

  const handleResendVerification = useCallback(
    async (user: AppUser) => {
      if (user.isVerified) return;

      setResendingUserId(user.id);
      try {
        await resendVerificationEmail({ id: user.id }).unwrap();
      } catch (error: any) {
        if (!error?.data?.message) {
          toast.error('Failed to resend verification email');
        }
      } finally {
        setResendingUserId(null);
      }
    },
    [resendVerificationEmail]
  );

  const renderTableRow = useCallback(
    (row: AppUser): React.ReactNode[] => [
      <button
        key="fullName"
        type="button"
        className="font-medium text-brand-600 truncate text-left hover:underline outline-none focus:ring-1 focus:ring-brand-500 rounded"
        onClick={() => navigate(`/users/view/${row.id}`)}
      >
        {row.fullName}
      </button>,
      <span
        key="email"
        className="text-sm text-neutral-600 dark:text-neutral-400 truncate"
      >
        {row.email ?? '—'}
      </span>,
      <VerificationBadge key="isVerified" isVerified={row.isVerified} />,
      <div key="status" className="flex items-center gap-3">
        <Toggle
          checked={row.status === 'active'}
          isLoading={togglingUserId === row.id && isStatusUpdating}
          onChange={() => handleStatusToggle(row)}
        />
        <StatusTag value={row.status} />
      </div>,
      <span key="eventCreated" className="text-sm text-center block">
        {row.eventCreated}
      </span>,
      <span key="eventJoined" className="text-sm text-center block">
        {row.eventJoined}
      </span>,
      <span
        key="createdDt"
        className="text-sm text-neutral-600 dark:text-neutral-400"
      >
        {formatDate(row.createdDt)}
      </span>,
      row.isVerified ? (
        <span
          key="actions"
          className="text-sm text-neutral-400 dark:text-neutral-500"
        >
          —
        </span>
      ) : (
        <button
          key="actions"
          type="button"
          onClick={() => handleResendVerification(row)}
          disabled={resendingUserId === row.id && isResendingVerification}
          className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Send className="h-3.5 w-3.5" />
          {resendingUserId === row.id && isResendingVerification
            ? 'Sending...'
            : 'Resend Link'}
        </button>
      ),
    ],
    [
      handleResendVerification,
      isResendingVerification,
      navigate,
      resendingUserId,
    ]
  );

  return (
    <UniversalModuleView<AppUser>
      moduleKey="users"
      title="Users"
      apiEndpoint="api/v1/user"
      listMethod="GET"
      columnsConfig={columns}
      rowKey={(row) => row.id}
      tableRowRenderer={renderTableRow}
      refreshToken={refreshToken}
      extraPayload={{ status: filterStatus || undefined, isDeleted: false }}
      hideFilterButton={true}
      extraActions={({ reset }) => (
        <div className="flex items-center gap-3">
          <div className="w-48">
            <Dropdown
              id="status-filter"
              value={filterStatus}
              setValue={(val) => setFilterStatus(String(val))}
              options={[
                { label: 'All Status', value: '' },
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
              ]}
              placeholder="All Status"
              appendTo="body"
            />
          </div>
          <IconButton
            icon={RefreshCw}
            title="Refresh"
            onClick={() => {
              handleRefresh();
              reset();
            }}
          />
        </div>
      )}
      searchPlaceholder="Search by name, email, or ID..."
      note={
        <>
          <strong>Note:</strong> Toggle user status between Active and Inactive.
          Inactive users will be blocked from logging in with the message:{' '}
          <em>"Your account has been locked by admin."</em> Click on any row to
          view detailed user information.
        </>
      }
      initialPageSize={10}
    />
  );
};

export default UsersPage;
